import { inject, Injectable, InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';

import { MockCannesWifiService } from '../../mocks/hunting-device/mock-cannes-wifi.service';
import { MockHuntingDeviceGate } from '../../mocks/hunting-device/mock-hunting-device-gate.service';

import type { CannesWifiService } from './cannes-wifi.service';

// Separate token for the real service so the facade can hold a real instance while the app-wide
// CannesWifiService token resolves to the facade itself (see app.config.ts).
export const REAL_CANNES_WIFI = new InjectionToken<CannesWifiService>('REAL_CANNES_WIFI');

/**
 * Routes every native-WiFi call to the real service or the mock based on MockHuntingDeviceGate,
 * so the dev toggle swaps them at runtime instead of build time.
 */
@Injectable()
export class CannesWifiFacade {
  // services
  private readonly real = inject(REAL_CANNES_WIFI);
  private readonly mock = inject(MockCannesWifiService);
  private readonly gate = inject(MockHuntingDeviceGate);

  public generatePassphrase(): string {
    return this.gate.isActive ? this.mock.generatePassphrase() : this.real.generatePassphrase();
  }

  public isReusablePassphrase(passphrase: string | undefined): passphrase is string {
    return this.gate.isActive ? this.mock.isReusablePassphrase(passphrase) : this.real.isReusablePassphrase(passphrase);
  }

  public hasLocationPermission(): Promise<boolean> {
    return this.gate.isActive ? this.mock.hasLocationPermission() : this.real.hasLocationPermission();
  }

  public requestLocationPermission(): Promise<void> {
    return this.gate.isActive ? this.mock.requestLocationPermission() : this.real.requestLocationPermission();
  }

  public connectToCannesAp(ssid: string, passphrase?: string): Promise<void> {
    return this.gate.isActive ? this.mock.connectToCannesAp(ssid, passphrase) : this.real.connectToCannesAp(ssid, passphrase);
  }

  public disconnectFromCannesAp(): Promise<void> {
    return this.gate.isActive ? this.mock.disconnectFromCannesAp() : this.real.disconnectFromCannesAp();
  }

  public watchDisconnect$(expectedSsid: string): Observable<void> {
    return this.gate.isActive ? this.mock.watchDisconnect$(expectedSsid) : this.real.watchDisconnect$(expectedSsid);
  }
}

// Compile-time guard: fails the build if the facade drifts from the real service's public API.
type _PublicApi = Pick<
  CannesWifiService,
  | 'generatePassphrase'
  | 'isReusablePassphrase'
  | 'hasLocationPermission'
  | 'requestLocationPermission'
  | 'connectToCannesAp'
  | 'disconnectFromCannesAp'
  | 'watchDisconnect$'
>;
const _assert: _PublicApi = undefined as unknown as CannesWifiFacade;

// Fails the build when the real service gains a public member the facade does not forward —
// a missing forward otherwise surfaces only as a runtime TypeError (the app-wide token
// resolves to the facade).
type _MissingFromFacade = Exclude<keyof CannesWifiService, keyof CannesWifiFacade>;
const _assertNoneMissing: Record<_MissingFromFacade, never> = {};
